import { zoneInput } from '@trafiku/contracts';
import { useEffect } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { FormActions } from '@/components/form-actions';
import { IssuesBadge, useIssuesById } from '@/components/issues-badge';
import { arrowIcon } from '@/components/map/icons';
import { arrowLayer, fc, fillLayer, GeoJsonLayer } from '@/components/map/layers';
import { MapTag } from '@/components/map/map-tag';
import { boundsOf, MapView } from '@/components/map/map-view';
import { OverviewMap } from '@/components/map/overview-map';
import { ShapeEditor } from '@/components/map/shape-editor';
import { ReadOnlyNotice } from '@/components/read-only-notice';
import { ResourceList } from '@/components/resource-list';
import { Combobox } from '@/components/ui/combobox';
import { ColorInput, Field, Input, Switch, Textarea } from '@/components/ui/form';
import { Badge, Card, ColorDot, PageHeader, Spinner } from '@/components/ui/layout';
import { useAuth } from '@/lib/auth';
import { useFormState } from '@/lib/form';
import { t } from '@/lib/i18n';
import { lineOptions, useNetwork, zoneFencesCollection, zonesCollection } from '@/lib/network';
import { useItem, useSave } from '@/lib/resources';
import type { Zone } from '@/lib/types';
import { checkZone, travelOrdered, type ZoneCheck } from '@/lib/zone-check';

export function ZonesPage() {
  const network = useNetwork();
  const { can } = useAuth();
  const lineName = (id?: string | null) => network.data?.lines.find((l) => l._id === id)?.name ?? '—';
  const issues = useIssuesById('lines');
  return (
    <ResourceList<Zone>
      title={t('Lines')}
      subtitle={t(
        'A line with its two directions. Buses assigned to a line switch direction at the terminus fences.',
      )}
      map={({ hovered, hover }) => (
        <OverviewMap
          data={zonesCollection(network.data)}
          kind="line"
          hovered={hovered}
          onHover={hover}
          basePath="/lines"
        >
          <GeoJsonLayer
            data={zoneFencesCollection(network.data)}
            layers={[fillLayer(['get', 'color'], 0.25)]}
          />
        </OverviewMap>
      )}
      resource="zones"
      basePath="/lines"
      canCreate={can('admin')}
      newLabel={t('New line')}
      columns={[
        {
          header: t('Line'),
          cell: (z) => (
            <span className="flex items-center gap-2 font-medium">
              <ColorDot color={z.color} /> {z.bus_sign} · {z.name}
            </span>
          ),
        },
        { header: t('Going'), cell: (z) => lineName(z.on_line_going) },
        { header: t('Return'), cell: (z) => lineName(z.on_line_return) },
        { header: t('Order'), cell: (z) => z.sort },
        {
          header: t('Status'),
          cell: (z) =>
            z.is_active ? (
              <Badge tone="green">{t('In service')}</Badge>
            ) : (
              <Badge>{t('Not in service')}</Badge>
            ),
        },
        { header: t('Issues'), cell: (z) => <IssuesBadge issues={issues.get(z._id)} /> },
      ]}
    />
  );
}

const GOING = '#2563eb';
const RETURN = '#ea580c';

export function ZoneEditPage() {
  const { id } = useParams();
  const isNew = id === 'new';
  const item = useItem<Zone>('zones', id);
  const network = useNetwork();
  const save = useSave<Zone>('zones', id);
  const navigate = useNavigate();
  const { can } = useAuth();
  const readOnly = !can('admin');
  const form = useFormState({
    name: '',
    description: '',
    bus_sign: '',
    color: '#0b3f82',
    sort: 0,
    is_active: false,
    on_line_going: null as string | null,
    on_line_return: null as string | null,
    geometry_going: null as Zone['geometry_going'] | null,
    geometry_return: null as Zone['geometry_return'] | null,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: load once per item
  useEffect(() => {
    const z = item.data;
    if (z)
      form.setValues({
        name: z.name,
        description: z.description ?? '',
        bus_sign: z.bus_sign,
        color: z.color,
        sort: z.sort,
        is_active: z.is_active,
        on_line_going: z.on_line_going ?? null,
        on_line_return: z.on_line_return ?? null,
        geometry_going: z.geometry_going,
        geometry_return: z.geometry_return,
      });
  }, [item.data]);

  // Old bookmark of a direction (directions used to be at /lines/:id).
  if (!isNew && item.isError && network.data?.lines.some((l) => l._id === id))
    return <Navigate to={`/directions/${id}`} replace />;
  if ((!isNew && item.isLoading) || !network.data) return <Spinner />;
  const { values, set, errors } = form;
  const lines = network.data.lines;
  const stationName = new Map(network.data.stations.map((st) => [st._id, st.name]));
  /** "First station → last station" of a line. */
  const endpoints = (lineId?: string | null) => {
    const l = lines.find((x) => x._id === lineId);
    const ids = l?.ordered_stations ?? [];
    return ids.length
      ? `${stationName.get(ids[0]!) ?? '?'} → ${stationName.get(ids.at(-1)!) ?? '?'}`
      : undefined;
  };
  /** "First → last station", with links to the direction and its timetable. */
  const directionHint = (lineId?: string | null) =>
    lineId ? (
      <span className="flex flex-wrap items-center gap-x-2">
        <span>{endpoints(lineId)}</span>
        <Link to={`/directions/${lineId}`} className="font-medium text-brand-700 hover:underline">
          {t('Edit direction')}
        </Link>
        <Link to={`/schedules/directions/${lineId}`} className="font-medium text-brand-700 hover:underline">
          {t('Timetable')}
        </Link>
      </span>
    ) : undefined;
  const endpointOptions = lineOptions(lines).map((o) => ({ ...o, hint: endpoints(o.value) ?? o.hint }));
  const chosen = [values.on_line_going, values.on_line_return];
  const check = checkZone(values, network.data);
  const stationsById = new Map(network.data.stations.map((st) => [st._id, st]));
  const travelLine = (lineId: string | null) => {
    const l = lines.find((x) => x._id === lineId);
    return fc(
      l
        ? [
            {
              type: 'Feature',
              geometry: { type: 'LineString', coordinates: travelOrdered(l, stationsById) },
              properties: {},
            },
          ]
        : [],
    );
  };

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        const ok = await form.submit(zoneInput, (d) => save.mutateAsync(d));
        if (ok) navigate('/lines');
      }}
    >
      <PageHeader title={isNew ? t('New line') : `${values.bus_sign} · ${values.name}`} back="/lines" />
      {readOnly && <ReadOnlyNotice />}
      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        <Card>
          <fieldset disabled={readOnly} className="min-w-0 space-y-4">
            <Field label={t('Name')} error={errors.name}>
              {(fid) => (
                <Input
                  id={fid}
                  value={values.name}
                  onChange={(e) => set('name', e.target.value)}
                  invalid={!!errors.name}
                />
              )}
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('Bus sign')} error={errors.bus_sign} hint={t('Shown on buses, e.g. 1 or 3A')}>
                {(fid) => (
                  <Input
                    id={fid}
                    value={values.bus_sign}
                    onChange={(e) => set('bus_sign', e.target.value)}
                    invalid={!!errors.bus_sign}
                  />
                )}
              </Field>
              <Field label={t('Order in app')} error={errors.sort}>
                {(fid) => (
                  <Input
                    id={fid}
                    type="number"
                    value={values.sort}
                    onChange={(e) => set('sort', Number(e.target.value))}
                  />
                )}
              </Field>
            </div>
            <Field label={t('Colour')} error={errors.color} hint={t('Also applied to both directions.')}>
              {() => (
                <ColorInput value={values.color} onChange={(v) => set('color', v)} invalid={!!errors.color} />
              )}
            </Field>
            <Field
              label={t('Going direction')}
              error={errors.on_line_going}
              hint={directionHint(values.on_line_going)}
            >
              {(fid) => (
                <Combobox
                  id={fid}
                  value={values.on_line_going ?? ''}
                  onChange={(id) => set('on_line_going', id || null)}
                  emptyLabel={t('— none —')}
                  placeholder={t('Search direction…')}
                  options={endpointOptions}
                />
              )}
            </Field>
            <Field
              label={t('Return direction')}
              error={errors.on_line_return}
              hint={directionHint(values.on_line_return)}
            >
              {(fid) => (
                <Combobox
                  id={fid}
                  value={values.on_line_return ?? ''}
                  onChange={(id) => set('on_line_return', id || null)}
                  emptyLabel={t('— none —')}
                  placeholder={t('Search direction…')}
                  options={endpointOptions}
                />
              )}
            </Field>
            <Field label={t('Description')}>
              {(fid) => (
                <Textarea
                  id={fid}
                  value={values.description}
                  onChange={(e) => set('description', e.target.value)}
                />
              )}
            </Field>
            <Switch
              checked={values.is_active}
              onChange={(v) => set('is_active', v)}
              label={t('In service (buses visible in the app)')}
            />
            <FenceGuide
              check={check}
              errors={[
                errors.geometry_going && `${t('Going fence')}: ${errors.geometry_going}`,
                errors.geometry_return && `${t('Return fence')}: ${errors.geometry_return}`,
              ].filter((e): e is string => !!e)}
              onSwap={() =>
                form.setValues((v) => ({
                  ...v,
                  geometry_going: v.geometry_return,
                  geometry_return: v.geometry_going,
                }))
              }
            />
          </fieldset>
          <div className="mt-6">
            <FormActions
              saving={save.isPending}
              resource="zones"
              id={id}
              backTo="/lines"
              canDelete={can('admin')}
              canSave={can('admin')}
              deleteMessage={t('Lines with assigned buses cannot be deleted.')}
            />
          </div>
        </Card>
        <MapView
          className="h-[680px]"
          fit={boundsOf(
            values.geometry_going?.coordinates,
            values.geometry_return?.coordinates,
            lines.filter((l) => chosen.includes(l._id)).map((l) => l.geometry.coordinates),
          )}
        >
          {/* Each line in its direction of travel, offset to its right so both stay visible. */}
          <GeoJsonLayer data={travelLine(values.on_line_going)} layers={directedLine(GOING)} />
          <GeoJsonLayer data={travelLine(values.on_line_return)} layers={directedLine(RETURN)} />
          {check.a && (
            <MapTag at={check.a.pos} color={GOING}>
              A · {check.a.name}
            </MapTag>
          )}
          {check.b && (
            <MapTag at={check.b.pos} color={RETURN}>
              B · {check.b.name}
            </MapTag>
          )}
          <ShapeEditor
            readOnly={readOnly}
            slots={[
              {
                key: 'geometry_going',
                label: t('Going fence'),
                kind: 'Polygon',
                color: GOING,
                value: values.geometry_going,
              },
              {
                key: 'geometry_return',
                label: t('Return fence'),
                kind: 'Polygon',
                color: RETURN,
                value: values.geometry_return,
              },
            ]}
            onChange={(key, g) =>
              set(key as 'geometry_going' | 'geometry_return', (g as Zone['geometry_going']) ?? null)
            }
          />
        </MapView>
      </div>
    </form>
  );
}

function directedLine(color: string) {
  return [
    {
      type: 'line' as const,
      layout: { 'line-cap': 'round' as const, 'line-join': 'round' as const },
      paint: { 'line-color': color, 'line-width': 4, 'line-opacity': 0.75, 'line-offset': 4 },
    },
    arrowLayer(arrowIcon(color)),
  ];
}

/** What the fences mean, and whether this zone's fences sit where they belong. */
function FenceGuide({ check, errors, onSwap }: { check: ZoneCheck; errors: string[]; onSwap: () => void }) {
  const swapped = check.going?.swapped || check.return?.swapped;
  const row = (ok: boolean | null, text: string) => (
    <li className="flex gap-1.5">
      <span className={ok === null ? 'text-slate-400' : ok ? 'text-emerald-600' : 'text-amber-600'}>
        {ok === null ? '•' : ok ? '✓' : '!'}
      </span>
      <span>{text}</span>
    </li>
  );
  const fenceRow = (f: ZoneCheck['going'], own: 'A' | 'B', label: string, terminus?: { name: string }) =>
    !f
      ? row(false, t('{fence}: not drawn yet.', { fence: label }))
      : !terminus
        ? row(null, t('{fence}: drawn (choose the directions to check its position).', { fence: label }))
        : f.ok
          ? row(true, t('{fence} is at {p} ({name}).', { fence: label, p: own, name: terminus.name }))
          : f.swapped
            ? row(
                false,
                t('{fence} is at the other end, not at {p}: the fences look swapped.', {
                  fence: label,
                  p: own,
                }),
              )
            : row(
                false,
                t('{fence} is {m} m from {p} ({name}).', {
                  fence: label,
                  p: own,
                  m: f.metresToOwn ?? '?',
                  name: terminus.name,
                }),
              );
  return (
    <div className="space-y-2 rounded-md bg-slate-50 p-3 text-xs text-slate-600">
      <p className="font-semibold text-slate-800">{t('How the direction is decided')}</p>
      <p>
        {t(
          'A bus that enters the going fence starts the going direction (A → B); one that enters the return fence starts the return direction (B → A). So the going fence belongs at A, the return fence at B.',
        )}
      </p>
      <p className="text-slate-500">
        {t(
          'The new direction logic also follows how the bus moves along both directions, so a missed fence no longer keeps a bus in the wrong direction.',
        )}
      </p>
      <ul className="space-y-1 pt-1">
        {fenceRow(check.going, 'A', t('Going fence'), check.a)}
        {fenceRow(check.return, 'B', t('Return fence'), check.b)}
        {check.returnMatches !== null &&
          row(
            check.returnMatches,
            check.returnMatches
              ? t('The return direction starts at B and ends at A.')
              : t('The return direction does not start at B and end at A: check the chosen directions.'),
          )}
      </ul>
      {swapped && (
        <button type="button" onClick={onSwap} className="font-semibold text-brand-700 hover:underline">
          {t('Swap the two fences')}
        </button>
      )}
      {errors.map((e) => (
        <p key={e} className="text-red-600">
          {e}
        </p>
      ))}
    </div>
  );
}
